<div class="p-3">
  <div class="page-title mb-4">
    <h3>Availability Reports</h3>
    <p class="m-0 text-muted">See resident bookings against the slots you've opened, and check each resident's blackout dates.</p>
  </div>

  <p-tabView>
    <p-tabPanel header="Summary" leftIcon="pi pi-calendar">

      <div class="d-flex align-item-center justify-content-between flex-column flex-sm-row">
        <p class="text-sm text-black m-0">This week</p>
      <div class="week-nav mb-4">
        <button pButton icon="pi pi-chevron-left" class="p-button-text p-button-sm nav-btn"
          (click)="prevWeek()"></button>
        <span class="week-label">{{ weekLabel }}</span>
        <button pButton icon="pi pi-chevron-right" class="p-button-text p-button-sm nav-btn"
          (click)="nextWeek()"></button>
        </div>
      </div>

      <div *ngIf="loading" class="d-flex justify-content-center py-5">
        <p-progressSpinner strokeWidth="4"></p-progressSpinner>
      </div>

      <ng-container *ngIf="!loading">

        <div class="stat-grid mb-4">
          <div class="stat-card">
            <div class="stat-card-body">
              <div class="stat-card-title">Open Days</div>
              <div class="stat-card-value">{{ openDays }}</div>
              <div class="stat-card-sub">days property is available</div>
            </div>
            <div class="stat-card-icon">
              <i class="pi pi-building"></i>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-card-body">
              <div class="stat-card-title">Closed Days</div>
              <div class="stat-card-value">{{ closedDays }}</div>
              <div class="stat-card-sub">days marked unavailable</div>
            </div>
            <div class="stat-card-icon">
              <i class="pi pi-ban"></i>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-card-body">
              <div class="stat-card-title">Total Bookings</div>
              <div class="stat-card-value">{{ totalBookings }}</div>
              <div class="stat-card-sub">resident slots booked</div>
            </div>
            <div class="stat-card-icon">
              <i class="pi pi-calendar"></i>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-card-body">
              <div class="stat-card-title">Residents</div>
              <div class="stat-card-value">{{ residents }}</div>
              <div class="stat-card-sub">combined across the week</div>
            </div>
            <div class="stat-card-icon">
              <i class="pi pi-users"></i>
            </div>
          </div>

        </div>

        <p-table [value]="weekRows" dataKey="date"
          [expandedRowKeys]="expandedRows"
          styleClass="p-datatable-sm week-table">

          <ng-template pTemplate="header">
            <tr>
              <th style="min-width:140px">Date / Day</th>
              <th style="min-width:180px; text-align: center;">Property Availability</th>
              <th style="min-width:100px">Residents</th>
              <th style="min-width:160px">Uncovered Slots</th>
              <th style="min-width:100px">Status</th>
              <th style="width:3rem"></th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-row let-expanded="expanded">
            <tr [pRowToggler]="row" style="cursor:pointer">

              <td>
                <span class="fw-semibold d-block text-black">{{ row.dayName }}</span>
                <small class="text-muted">{{ formatDate(row.date) }}</small>
              </td>

              <td>
                <ng-container *ngIf="row.propertySlots.length; else noSlots">
                  <div class="slots-wrap">
                    <span class="slot-pill" *ngFor="let s of row.propertySlots | slice:0:3">
                      {{ formatSlot(s) }}
                    </span>
                    <span *ngIf="row.propertySlots.length > 3"
                      class="more-badge"
                      [pTooltip]="slotTooltip(row.propertySlots.slice(3))"
                      tooltipPosition="top">
                      +{{ row.propertySlots.length - 3 }} more
                    </span>
                  </div>
                </ng-container>
                <ng-template #noSlots>
                  <span class="not-available">Not Available</span>
                </ng-template>
              </td>

              <td>
                <span *ngIf="row.availableResidents.length; else noRes" class="resident-pill">
                  <i class="pi pi-users"></i> {{ row.availableResidents.length }}
                </span>
                <ng-template #noRes>
                  <span class="resident-pill">
                    <i class="pi pi-users"></i> 0
                  </span>
                </ng-template>
              </td>

              <td>
                <ng-container *ngIf="row.uncoveredSlots.length; else allCovered">
                  <span class="uncovered-pill"
                    [pTooltip]="slotTooltip(row.uncoveredSlots)"
                    tooltipPosition="top">
                    <i class="pi pi-ban"></i>
                    {{ row.uncoveredSlots.length }} uncovered
                  </span>
                </ng-container>
                <ng-template #allCovered>
                  <span class="text-muted">—</span>
                </ng-template>
              </td>

              <td>
                <p-tag [value]="statusLabel(row.status)"
                  [severity]="statusSeverity(row.status)"></p-tag>
              </td>

              <td>
                <i class="pi" [ngClass]="expanded ? 'pi-chevron-up' : 'pi-chevron-down'"
                  style="color:#9ca3af; font-size:0.85rem"></i>
              </td>

            </tr>
          </ng-template>

          <ng-template pTemplate="rowexpansion" let-row>
            <tr class="expansion-row">
              <td colspan="6">
                <div class="bookings-panel">
                  <p class="bookings-title mb-2">
                    Resident Availability — {{ row.dayName }}
                  </p>
                  <div *ngIf="row.availableResidents.length; else noResidents" class="bookings-list">
                    <div class="booking-item" *ngFor="let r of row.availableResidents">
                      <div class="row">
                        <div class="col-6">
                          <span class="booking-resident">{{ r.name }}</span>
                        </div>
                        <div class="col-6">
                          <!-- <span class="booking-sep">·</span> -->
                           <div class="d-flex flex-wrap gap-1">
                             <span class="text-primry text-sm text-nowrap"
                               *ngFor="let s of r.slots | slice:0:2">
                               {{ formatSlot(s) }}
                             </span>
                             <span *ngIf="r.slots.length > 2"
                               class="more-badge"
                               [pTooltip]="slotTooltip(r.slots.slice(2))"
                               tooltipPosition="top">
                               +{{ r.slots.length - 2 }} more
                             </span>
                           </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <ng-template #noResidents>
                    <p class="text-muted small mb-0">No residents available this day.</p>
                  </ng-template>
                </div>
              </td>
            </tr>
          </ng-template>

        </p-table>

      </ng-container>

    </p-tabPanel>

    <p-tabPanel header="Resident Blackout" leftIcon="pi pi-ban">
      <div class="rb-layout">

        <div class="rb-left">

          <div class="mb-3">
            <label class="fw-semibold mb-1 d-block" style="font-size:0.875rem">Select Resident</label>
            <p-dropdown [options]="tenants" [(ngModel)]="selectedTenantId"
              optionLabel="label" optionValue="value"
              placeholder="Search resident…" [filter]="true"
              filterPlaceholder="Search…" [style]="{'width':'100%'}"
              (onChange)="onTenantSelect()">
            </p-dropdown>
          </div>

          <div class="rb-viewing mb-3" *ngIf="selectedTenantName">
            <p class="rb-viewing__label mb-1">Currently viewing</p>
            <p class="rb-viewing__name">{{ selectedTenantName }}</p>
          </div>

          <div class="rb-widgets mb-3" *ngIf="selectedTenantId && !blackoutLoading">
            <div class="rb-widget rb-widget--grey">
              <div class="rb-widget__value">{{ fullDayCount }}</div>
              <div class="rb-widget__label">Full Day</div>
            </div>
            <div class="rb-widget rb-widget--yellow">
              <div class="rb-widget__value">{{ partialCount }}</div>
              <div class="rb-widget__label">Partial Day</div>
            </div>
          </div>

          <div class="rb-legend" *ngIf="selectedTenantId">
            <div class="rb-legend-item">
              <span class="rb-legend-box rb-legend-box--grey"></span>
              <span>Full-day blackout</span>
            </div>
            <div class="rb-legend-item">
              <span class="rb-legend-box rb-legend-box--yellow"></span>
              <span>Partial blackout (time shown)</span>
            </div>
          </div>

          <p class="text-muted small mt-3" *ngIf="!selectedTenantId">
            Select a resident to view their blackout calendar.
          </p>

        </div>

        <div class="rb-right">
          <div *ngIf="blackoutLoading" class="d-flex justify-content-center py-5">
            <p-progressSpinner strokeWidth="4"></p-progressSpinner>
          </div>
          <div *ngIf="!blackoutLoading && selectedTenantId" class="rb-calendar-holder">
            <full-calendar [options]="blackoutCalendarOptions"></full-calendar>
          </div>
          <div *ngIf="!selectedTenantId" class="rb-calendar-placeholder">
            <i class="pi pi-calendar" style="font-size:3rem;color:#d1d5db"></i>
            <p class="text-muted mt-2">Select a resident to view their calendar</p>
          </div>
        </div>

      </div>
    </p-tabPanel>

  </p-tabView>
</div>
